// Body of the "Getting Started with Companion" post. Mirrors the design's
// long-form copy with inline Companion call-outs and screenshot placeholders.
import type { CSSProperties, ReactNode } from "react";

import { MidArticleDemoBanner } from "./MidArticleDemoBanner";
import { ScreenshotPlaceholder } from "./ScreenshotPlaceholder";

const H2_CLASS =
  "font-companion mt-14 mb-3.5 text-[clamp(26px,3vw,34px)] font-bold leading-[1.2] tracking-[-0.02em] text-primary";

const H3_CLASS =
  "font-companion mt-8 mb-2.5 text-[clamp(20px,2.2vw,23px)] font-semibold leading-[1.3] tracking-[-0.01em] text-grindigo-800";

const P_CLASS =
  "mb-[22px] font-body text-[19px] leading-[1.7] text-grindigo-800 [text-wrap:pretty]";

const UL_CLASS =
  "mb-[22px] font-body text-[18px] leading-[1.65] text-grindigo-800 list-disc pl-[22px]";

const LI_CLASS = "mb-2 [text-wrap:pretty]";

const CALLOUT_CLASS =
  "my-2 mb-7 rounded-2xl border border-[#E9E5FF] bg-[#F5F3FF] px-6 py-5";

type CompanionAccent = "quill" | "summi" | "sage" | "otto";

const accentColor: Record<CompanionAccent, string> = {
  quill: "#D97706",
  summi: "#DB2777",
  sage: "#059669",
  otto: "#2563EB",
};

function CompanionTag({
  emoji,
  name,
  accent,
}: {
  emoji: string;
  name: string;
  accent: CompanionAccent;
}) {
  return (
    <span
      className="font-bold"
      style={{ color: accentColor[accent] } as CSSProperties}
    >
      {name} {emoji}
    </span>
  );
}

function GoodToKnowItem({ title, body }: { title: string; body: string }) {
  return (
    <div className="grid grid-cols-[auto_1fr] items-start gap-3.5 rounded-[14px] border border-grindigo-100 bg-white px-4 py-4">
      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-indigo-100 bg-indigo-50 text-indigo-600">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div>
        <div className="font-companion mb-1 text-base font-semibold tracking-[-0.005em] text-primary">
          {title}
        </div>
        <div className="font-body text-[15px] leading-[1.55] text-grindigo-600">
          {body}
        </div>
      </div>
    </div>
  );
}

function Callout({ children }: { children: ReactNode }) {
  return (
    <div className={CALLOUT_CLASS}>
      <p className={`${P_CLASS} m-0`}>{children}</p>
    </div>
  );
}

export function GettingStartedBody() {
  return (
    <article className="px-[clamp(24px,5vw,80px)] pb-24">
      <div className="mx-auto max-w-[720px]">
        <p className={P_CLASS}>
          Companion is your AI assistant inside Scribenote. It amplifies your
          ability as a veterinary professional by helping you with
          documentation, research, client communication, and more. It&apos;s
          meant to support your clinical judgement (not replace it!) so you can
          spend more of your day focused on the patient in front of you.
        </p>

        <p className={P_CLASS}>
          Just open Companion inside Scribenote, ask for what you need, and the
          right assistant shows up:{" "}
          <CompanionTag emoji="🦔" name="Quill" accent="quill" /> for
          documentation, <CompanionTag emoji="🐘" name="Summi" accent="summi" />{" "}
          for patient histories, and{" "}
          <CompanionTag emoji="🐢" name="Sage" accent="sage" /> for research.{" "}
          <CompanionTag emoji="🐕" name="Otto" accent="otto" /> will also show
          up from time to time to manage the other Companions and provide
          support.
        </p>

        <Callout>
          The vets who get the most out of Companion are the ones who treat it
          like a colleague. They ask it for what they need, teach it how they
          work, and let it take tasks off their plate. This post will show you
          how, including what some vets have tried and the results they got.{" "}
          <strong>
            Your experience might look different depending on how you use it.
          </strong>
        </Callout>

        <h2 className={H2_CLASS}>See what it can do</h2>

        <h3 className={H3_CLASS}>Before the appointment</h3>
        <p className={P_CLASS}>
          Say you&apos;ve got a new patient coming in for an allergy consult: a
          dog named Bella with a long history from another clinic. She&apos;s
          been licking her paws, she&apos;s itchy, and she keeps getting ear
          infections. You just got her records as a 30-page PDF, and you&apos;ve
          got maybe 5 minutes before her appointment.
        </p>
        <p className={P_CLASS}>
          Instead of speed-reading and hoping you catch the important stuff, you
          upload the PDF to Companion and ask{" "}
          <em>
            &ldquo;I have an appointment with Bella this morning for an allergy
            flare-up. What do I need to know?&rdquo;
          </em>{" "}
          By the time you&apos;re back from grabbing your morning coffee,{" "}
          <CompanionTag emoji="🐘" name="Summi" accent="summi" /> has pulled out
          everything you need to know about Bella&apos;s history before your
          appointment.
        </p>
        <ScreenshotPlaceholder
          companion="Summi"
          src="/blog/getting-started-with-companion/chats/Bella%20History%20Chat.html"
          caption="✨ Summi pulls out the headline derm timeline, current meds, vaccine status, and the open threads worth raising with the client today. ✨"
        />

        <p className={P_CLASS}>
          From there, you ask Companion to build you a dermatology template you
          can use in Bella&apos;s appointment.{" "}
          <CompanionTag emoji="🦔" name="Quill" accent="quill" /> then makes you
          a template including pruritus scoring, seasonality, full dermatologic
          exam by region, and more.
        </p>
        <ScreenshotPlaceholder
          companion="Quill"
          src="/blog/getting-started-with-companion/chats/Bella%20Template%20Chat.html"
          caption="✨ Quill creates an Adaptive Template for allergy exams. ✨"
          aspect="5/4"
        />

        <h3 className={H3_CLASS}>During the appointment</h3>
        <p className={P_CLASS}>
          You record the appointment in Scribenote like you normally would. It
          picks up everything, including the tape tests, the ear swabs, your
          decision to hold off on vaccines until she&apos;s feeling better, the
          medications you&apos;re sending her home with, and the client&apos;s
          decision to decline the 8-week food trial.
        </p>

        <h3 className={H3_CLASS}>After the appointment</h3>
        <p className={P_CLASS}>
          The client decides at the last minute that they want to try the
          prescription diet, so you tell Companion.{" "}
          <CompanionTag emoji="🦔" name="Quill" accent="quill" /> offers to add
          the 8-week food trial to the plan section of your note, and you
          accept. Then, you ask Companion to put together a food trial handout
          in layman&apos;s terms.{" "}
          <CompanionTag emoji="🦔" name="Quill" accent="quill" /> drafts you a
          plain-language handout explaining the 8-week food trial that you can
          give to your client.
        </p>
        <ScreenshotPlaceholder
          companion="Quill"
          src="/blog/getting-started-with-companion/chats/Bella%20Food%20Trial%20Chat.html"
          caption="✨ Quill drafts a plain-language 8-week food trial handout for the client. ✨"
        />
        <p className={P_CLASS}>
          You wonder, how does gut microbiome composition influence the
          development and severity of allergic skin conditions in dogs? So you
          ask Companion, and{" "}
          <CompanionTag emoji="🐢" name="Sage" accent="sage" /> gives you the
          answer.
        </p>
        <ScreenshotPlaceholder
          companion="Sage"
          src="/blog/getting-started-with-companion/chats/Bella%20Microbiome%20Chat.html"
          caption="✨ Sage pulls the current literature on the gut–skin axis in canine atopic dermatitis. ✨"
        />
        <p className={P_CLASS}>
          Finally, you generate a client summary from the note. Then, you ask{" "}
          <CompanionTag emoji="🦔" name="Quill" accent="quill" /> to edit it to
          only include the diagnostics performed, medications prescribed, and
          what to watch for at home.
        </p>
        <ScreenshotPlaceholder
          companion="Quill"
          src="/blog/getting-started-with-companion/chats/Bella%20Client%20Summary%20Chat.html"
          caption="✨ Quill drafts a client-friendly visit summary for Bella's owner. ✨"
          aspect="21/9"
        />

        <MidArticleDemoBanner />

        <h2 className={H2_CLASS}>Your first week together</h2>

        <p className={P_CLASS}>
          Think of Companion like a vet student on their first externship.
          They&apos;re smart, eager to learn, and helpful — but still learning
          how your clinic runs. It&apos;s going to get a lot right, but
          sometimes it won&apos;t, so always trust your own clinical judgement.
        </p>

        <p className={P_CLASS}>
          The good news is, Companion learns really fast. You only need to tell
          it something once, and it&apos;ll remember it. Your first week is when
          those lessons matter most, so it&apos;s best to teach it early:
        </p>

        <ul className={UL_CLASS}>
          <li className={LI_CLASS}>
            <strong>Tell it what you want it to remember.</strong> Companion has
            a memory, so if there&apos;s anything you&apos;d like it to
            remember, it&apos;s as easy as telling it:{" "}
            <em>
              &ldquo;remember that my clinic uses Simparica Trio for heartworm
              prevention from May 1st to December 1st.&rdquo;
            </em>{" "}
            Next time it comes up, Companion already knows, so you don&apos;t
            have to mention it again.
          </li>
        </ul>

        <ScreenshotPlaceholder
          companion="Otto"
          src="/blog/getting-started-with-companion/chats/Otto%20Memory%20Chat.html"
          caption="✨ Otto remembers what you tell it. ✨"
        />

        <ul className={UL_CLASS}>
          <li className={LI_CLASS}>
            <strong>Be specific and provide context.</strong> &ldquo;Write a
            discharge email for a diabetic cat going home on insulin&rdquo;
            gives Companion a lot more to work with than &ldquo;write a
            discharge email.&rdquo; Think of it like briefing a colleague: the
            more they know, the better!
          </li>
          <li className={LI_CLASS}>
            <strong>
              Tell it what to change, not what you don&apos;t like.
            </strong>{" "}
            &ldquo;Bold my note&apos;s subheadings&rdquo; is easier for
            Companion to understand and act on than &ldquo;this doesn&apos;t
            look right.&rdquo;
          </li>
        </ul>

        <ScreenshotPlaceholder
          companion="Otto"
          src="/blog/getting-started-with-companion/chats/Diabetic%20Discharge%20Chat.html"
          caption="✨ Be specific about what to change. Companion will iterate quickly. ✨"
        />

        <ul className={UL_CLASS}>
          <li className={LI_CLASS}>
            <strong>Start multiple chats at the same time.</strong> Ask
            Companion to draft a client email in one chat, look up a drug
            interaction in another. Companion takes its time to think through
            each task, so while one is working, you can start the next in
            parallel.
          </li>
        </ul>

        <Callout>
          <strong>
            Most important: if you&apos;re not sure whether something is
            possible, ask anyway.
          </strong>{" "}
          Companion is designed to try before declining, and if it can&apos;t
          help, it&apos;ll point you somewhere useful.
        </Callout>

        <p className={P_CLASS}>
          Companion can already handle documentation, patient summaries,
          research, discharge instructions, dosing calculations, and more. And
          just like that extern who gets sharper every week, it&apos;s only
          going to get better as you teach it how you work.
        </p>

        <h2 className={H2_CLASS}>Good to know</h2>

        <div className="my-2 mb-7 grid gap-3.5">
          <GoodToKnowItem
            title="Companion won't make clinical decisions."
            body="It surfaces information and helps with tasks. Diagnoses, treatment plans, and medical judgment stay with you — always."
          />
          <GoodToKnowItem
            title="It shows its work."
            body="Whether it's a dosing calculation or a summary pulled from past notes, Companion tells you what it did and where it got the information — so you can verify before you act."
          />
        </div>

        <h2 className={H2_CLASS}>We&apos;re building this with you</h2>
        <p className={P_CLASS}>
          Companion is new, and every vet&apos;s experience with it is going to
          look a little different. The way you use it, the results you get, and
          the things that surprise you are all going to depend on your workflow,
          your preferences, and what you ask it to do. We&apos;d love to hear
          about it.
        </p>
        <p className={P_CLASS}>
          Send us a note at{" "}
          <a
            href="mailto:submissions@scribenote.com"
            className="text-indigo-600 underline underline-offset-2"
          >
            submissions@scribenote.com
          </a>{" "}
          and tell us what your experience has been like. What did you use
          Companion for? What do you wish it could do? Your feedback directly
          shapes what we build next.
        </p>

        <h2 className={H2_CLASS}>The short version</h2>
        <p className={P_CLASS}>
          Companion lives inside and works alongside Scribenote. It fits right
          into your workflow before, during, and after the appointment. You
          simply ask for what you need, and the right Companion shows up to
          help. It remembers how you work so you don&apos;t have to repeat
          yourself, and it won&apos;t make clinical decisions, because those
          stay with you. You get 500 free Treats to start. Try it out, teach it
          how you work, and let us know how it goes! Get started today at{" "}
          <a
            href="https://app.scribenote.com/dashboard?companion"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline underline-offset-2"
          >
            app.scribenote.com
          </a>
          .
        </p>
      </div>
    </article>
  );
}
